import { FileDown, FileUp, Layers, Pencil, Plus, Power, Search } from 'lucide-react';
import { useState } from 'react';

import { useValorAtrasado } from '@/lib/atraso';
import { cx } from '@/lib/cx';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { Badge } from '@/ui/primitives/badge';
import { Button } from '@/ui/primitives/button';
import { Input } from '@/ui/primitives/input';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type CentroCusto, type CursorPage, type EmpresaDetalhe } from '@fiscoai/contracts';

import { gravar } from '../formulario';

import { baixarPlanilhaCentrosCusto, useAtualizarCentroCusto, useCentrosCusto } from './api';
import { DialogoCentroCusto } from './dialogo-centro-custo';
import { DialogoImportarCentrosCusto } from './dialogo-importar';
import { NATUREZA } from './rotulos';

interface Props {
  empresa: EmpresaDetalhe;
  podeEditar: boolean;
}

function Linha({
  cc,
  podeEditar,
  aoEditar,
  aoAlternar,
}: {
  cc: CentroCusto;
  podeEditar: boolean;
  aoEditar: () => void;
  aoAlternar: () => void;
}) {
  return (
    <li
      className={cx(
        'group flex items-center gap-3 px-4 py-2.5 transition-colors duration-[var(--duracao-rapida)] hover:bg-superficie-hover',
        !cc.ativo && 'text-texto-fraco',
      )}
    >
      <span className="w-24 shrink-0 truncate font-mono text-sm font-medium">{cc.codigo}</span>
      <span className="min-w-0 flex-1 truncate">{cc.descricao}</span>
      {!cc.ativo && <Badge tom="neutro">Inativo</Badge>}
      <Badge tom={NATUREZA[cc.natureza].tom}>{NATUREZA[cc.natureza].rotulo}</Badge>
      <span className="hidden w-20 text-right text-xs tabular text-texto-fraco sm:inline">
        {cc.usos === 0 ? 'sem uso' : `${String(cc.usos)} ${cc.usos === 1 ? 'item' : 'itens'}`}
      </span>
      {podeEditar && (
        <span className="flex gap-1 opacity-100 transition-opacity duration-[var(--duracao-rapida)] sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100">
          <Button
            variante="fantasma"
            tamanho="icone-sm"
            aria-label={`Editar ${cc.codigo}`}
            onClick={aoEditar}
          >
            <Pencil aria-hidden />
          </Button>
          <Button
            variante="fantasma"
            tamanho="icone-sm"
            aria-label={`${cc.ativo ? 'Inativar' : 'Reativar'} ${cc.codigo}`}
            onClick={aoAlternar}
          >
            <Power aria-hidden />
          </Button>
        </span>
      )}
    </li>
  );
}

function Cabecalho(p: {
  empresaId: string;
  podeEditar: boolean;
  aoImportar: () => void;
  aoNovo: () => void;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="grid gap-1">
        <h2 id="titulo-centros-custo" className="text-lg font-semibold">
          Centros de custo
        </h2>
        <p className="max-w-prose text-sm text-texto-suave">
          Informados pelo fiscal na nota (nota inteira ou item). A natureza decide se uso e consumo
          e serviço vão para Custo ou Despesa.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variante="fantasma"
          tamanho="sm"
          onClick={() => void baixarPlanilhaCentrosCusto(p.empresaId)}
        >
          <FileDown aria-hidden /> Exportar
        </Button>
        {p.podeEditar && (
          <>
            <Button tamanho="sm" onClick={p.aoImportar}>
              <FileUp aria-hidden /> Importar planilha
            </Button>
            <Button variante="primario" tamanho="sm" onClick={p.aoNovo}>
              <Plus aria-hidden /> Novo
            </Button>
          </>
        )}
      </div>
    </header>
  );
}

function CampoBusca({ valor, aoMudar }: { valor: string; aoMudar: (v: string) => void }) {
  return (
    <label className="relative block max-w-sm">
      <span className="sr-only">Buscar centro de custo</span>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-texto-fraco"
        aria-hidden
      />
      <Input
        type="search"
        className="pl-9"
        placeholder="Buscar por código ou descrição"
        value={valor}
        onChange={(e) => {
          aoMudar(e.target.value);
        }}
      />
    </label>
  );
}

function Resultado(p: {
  pagina: CursorPage<CentroCusto> | undefined;
  carregando: boolean;
  atualizando: boolean;
  termo: string;
  podeEditar: boolean;
  aoEditar: (cc: CentroCusto) => void;
  aoAlternar: (cc: CentroCusto) => void;
}) {
  if (p.carregando) return <Skeleton className="h-32 rounded-cartao" />;
  const itens = p.pagina?.items ?? [];
  if (itens.length === 0)
    return (
      <EstadoVazio
        icone={<Layers />}
        titulo={p.termo ? 'Nenhum centro de custo encontrado' : 'Nenhum centro de custo ainda'}
        texto={
          p.termo
            ? 'Tente outro código ou parte da descrição.'
            : 'Cadastre um a um ou importe a planilha (codigo;descricao;natureza;ativo).'
        }
        className="py-8"
      />
    );
  return (
    <>
      <ul
        className={cx(
          'divide-y divide-borda rounded-cartao border border-borda transition-opacity duration-[var(--duracao-rapida)]',
          p.atualizando && 'opacity-60',
        )}
        aria-label="Centros de custo"
      >
        {itens.map((cc) => (
          <Linha
            key={cc.id}
            cc={cc}
            podeEditar={p.podeEditar}
            aoEditar={() => {
              p.aoEditar(cc);
            }}
            aoAlternar={() => {
              p.aoAlternar(cc);
            }}
          />
        ))}
      </ul>
      {p.pagina?.nextCursor && (
        <p className="text-xs text-texto-fraco">
          Mostrando os primeiros {itens.length}. Refine a busca para achar os demais.
        </p>
      )}
    </>
  );
}

/**
 * Centros de custo da empresa (spec 0004): informados aqui, à mão ou por planilha — sem
 * integração. A natureza decide o grupo contábil de uso e consumo e serviço nas notas.
 */
export function AbaCentrosCusto({ empresa, podeEditar }: Props) {
  const [busca, setBusca] = useState('');
  const termo = useValorAtrasado(busca.trim());
  const lista = useCentrosCusto(empresa.id, termo);
  const atualizar = useAtualizarCentroCusto(empresa.id);
  const [dialogo, setDialogo] = useState<{ editando: CentroCusto | null } | null>(null);
  const [importando, setImportando] = useState(false);

  return (
    <section className="grid gap-4 py-6" aria-labelledby="titulo-centros-custo">
      <Cabecalho
        empresaId={empresa.id}
        podeEditar={podeEditar}
        aoImportar={() => {
          setImportando(true);
        }}
        aoNovo={() => {
          setDialogo({ editando: null });
        }}
      />
      <CampoBusca valor={busca} aoMudar={setBusca} />
      <Resultado
        pagina={lista.data}
        carregando={lista.isPending}
        atualizando={lista.isPlaceholderData}
        termo={termo}
        podeEditar={podeEditar}
        aoEditar={(cc) => {
          setDialogo({ editando: cc });
        }}
        aoAlternar={(cc) =>
          void gravar(
            () => atualizar.mutateAsync({ id: cc.id, ativo: !cc.ativo }),
            cc.ativo ? `${cc.codigo} inativado` : `${cc.codigo} reativado`,
          )
        }
      />
      <DialogoCentroCusto
        empresaId={empresa.id}
        editando={dialogo?.editando ?? null}
        aberto={dialogo !== null}
        aoFechar={() => {
          setDialogo(null);
        }}
      />
      <DialogoImportarCentrosCusto
        empresaId={empresa.id}
        aberto={importando}
        aoFechar={() => {
          setImportando(false);
        }}
      />
    </section>
  );
}
